import styled from 'styled-components';
import { PermissionContext } from '@ardoq/access-control';
import { ArdoqId } from '@ardoq/api-types';
import {
  Drawer,
  ResolvedComponentIcon,
  SidebarContent,
  type OpenEditPriorityFieldsProps,
} from '@ardoq/component-overview';
import { EnhancedScopeData } from '@ardoq/data-model';
import { connect } from '@ardoq/rxbeach';
import { detailsDrawer$, DetailsDrawerCommands } from './detailsDrawer$';
import { handleUpdateDetailsDrawer } from './routines';
import { zIndex } from '@ardoq/design-tokens';
import { Box } from '@ardoq/layout';
import { NoPriorityFieldsBanner } from 'priorityFields/NoPriorityFieldsBanner';
import { getOpenEditPriorityFieldsLinkProps } from 'priorityFields/openEditPriorityFieldsProps';
import { type I18nLocale } from '@ardoq/i18n';
import { toSidebarContentLabels } from 'componentOverview2025/componentOverviewLabels';

const DetailsDrawerContainer = styled.div`
  position: fixed;
  right: 0;
  height: 100%;
  z-index: ${zIndex.DETAILS_DRAWER};
`;

type DetailsDrawerProps = {
  scopeData: EnhancedScopeData | null;
  componentId: ArdoqId | null;
  bottomOffsetHeight?: number | string;
  commands: DetailsDrawerCommands;
  permissionContext: PermissionContext;
  priorityFieldNames: string[];
  hasDiscoverInCore: boolean;
  isAdmin: boolean;
  openEditPriorityFieldsProps: OpenEditPriorityFieldsProps;
  i18nLocale: I18nLocale;
};

const DetailsDrawerBase = ({
  scopeData,
  componentId,
  bottomOffsetHeight,
  commands,
  permissionContext,
  priorityFieldNames,
  hasDiscoverInCore,
  isAdmin,
  openEditPriorityFieldsProps,
  i18nLocale,
}: DetailsDrawerProps) => {
  if (!scopeData || !componentId) return null;
  const component = scopeData.componentsById[componentId];
  const sidebarContentLabels = toSidebarContentLabels(i18nLocale);

  const noPriorityFieldsBanner =
    hasDiscoverInCore && priorityFieldNames.length === 0 && isAdmin ? (
      <NoPriorityFieldsBanner
        configureLinkProps={getOpenEditPriorityFieldsLinkProps(component.type)}
        componentTypeName={component.type}
        i18nLocale={i18nLocale}
      />
    ) : null;

  return (
    <DetailsDrawerContainer
      style={{
        maxHeight: `calc(100% - ${bottomOffsetHeight ?? 0}px)`,
      }}
    >
      <Drawer
        onClose={() => commands.hideDetailsDrawer()}
        isOpen
        sidebarContent={
          <>
            <SidebarContent
              widgetId="details"
              scopeData={scopeData}
              component={component}
              permissionContext={permissionContext}
              priorityFieldNames={priorityFieldNames}
              noPriorityFieldsBanner={noPriorityFieldsBanner}
              openEditPriorityFieldsProps={openEditPriorityFieldsProps}
              labels={sidebarContentLabels}
            />
            <SidebarContent
              widgetId="references"
              scopeData={scopeData}
              component={component}
              permissionContext={permissionContext}
              labels={sidebarContentLabels}
            />
          </>
        }
        drawerTitle={component.name}
        drawerHeaderLeftContent={
          <ResolvedComponentIcon component={component} scopeData={scopeData} />
        }
        shouldCoverContent={false}
        shadow="shadowM"
      />
    </DetailsDrawerContainer>
  );
};

export const BoxWithMaxWidth = styled(Box)`
  max-width: 550px;
`;

export const DetailsDrawer = connect(
  DetailsDrawerBase,
  detailsDrawer$,
  handleUpdateDetailsDrawer
);
